import React, { useState } from "react";
import { Modal } from "antd";

import CompanyLogo from "components/CompanyLogo/CompanyLogo";
import addNotification from "helpers/notify";
import { switchMyCompany } from "api/companyMembership/companyMembership";
import {
  all_permissionsRef,
  company_permissionsRef,
  coordination_onlyRef,
  primary_packageRef,
  setJWT,
  tokenRef,
  user_permissionsRef,
} from "api";

// A new active company means a new permission set, project list and admin
// rights. Every one of those is cached somewhere in the app, so the switch
// stores the new session and starts the app again from the top rather than
// trying to find and refresh each cache.
export const enterSwitchedSession = (token, landing = "/") => {
  try {
    localStorage.setItem(tokenRef, token);
    [user_permissionsRef, company_permissionsRef, all_permissionsRef, coordination_onlyRef, primary_packageRef, "exporters"]
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* storage unavailable: the cookie still carries the session */ }
  setJWT(token);
  window.location.assign(landing);
};

// The company picker: shown after sign-in to anybody who belongs to more
// than one company, and from the header whenever they want to change.
//
//   memberships  rows from GET /company-membership/mine
//   onStay       the person picked the company they are already in
//   onCancel     omitted at sign-in, where a choice is required
//   landing      where the app opens after a switch
const CompanyPicker = ({ open, memberships, onStay, onCancel, landing = "/" }) => {
  const [busy, setBusy] = useState("");

  const pick = async (row) => {
    if (busy) return;
    if (row.active) {
      if (onStay) onStay(row);
      return;
    }
    setBusy(row.company);
    try {
      const res = await switchMyCompany(row.company);
      if (!res || !res.token) throw res || {};
      enterSwitchedSession(res.token, landing);
    } catch (e) {
      addNotification((e && e.err) || "Could not switch company", "danger");
      setBusy("");
    }
  };

  return (
    <Modal
      open={open}
      title="Choose a company"
      footer={null}
      closable={!!onCancel}
      maskClosable={!!onCancel}
      keyboard={!!onCancel}
      onCancel={onCancel || undefined}
      centered
    >
      <p className="mb-3" style={{ color: "#5f6368" }}>
        Your account belongs to more than one company. Pick the one you want to work in; you can change it later from the header.
      </p>
      {(memberships || []).map((row) => {
        const disabled = !!row.unavailable || (!!busy && busy !== row.company);
        return (
          <button
            key={row.company}
            type="button"
            disabled={disabled}
            onClick={() => pick(row)}
            title={row.unavailable || ""}
            style={{
              display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
              padding: 12, marginBottom: 8, borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
              background: row.active ? "#eef4f6" : "#fff",
              border: row.active ? "2px solid #3A5765" : "1px solid #d9d9d9",
              opacity: disabled ? 0.55 : 1,
            }}
          >
            <CompanyLogo logo={row.logo} name={row.companyName} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontWeight: 600, fontSize: 16 }}>{row.companyName || "Company"}</span>
              <span style={{ display: "block", fontSize: 13, color: "#5f6368" }}>
                {row.unavailable
                  ? row.unavailable
                  : [row.active ? "Current company" : "", row.companyAdmin ? "Company admin" : "Member"].filter(Boolean).join(" - ")}
              </span>
            </span>
            {busy === row.company ? <span style={{ fontSize: 13 }}>Switching...</span> : null}
          </button>
        );
      })}
    </Modal>
  );
};

export default CompanyPicker;
